// src/pages/user/UserList.tsx
import React, { useState } from "react";
import { User } from "../../../types/user";
import { useUserStore } from "../../../store/userStore";
import { CheckCheckIcon, Edit, Plus, Trash2 } from "lucide-react";
import { IconButton, IconTextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { UserDialog } from "./UserDialog";
import { toast } from "react-toastify";
import { EDataTable } from "../../../components/EDataTable";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { getTurkishRoleName } from "../../../types/role";
import { DataTableColumn } from "../../../types";

export const UserList = () => {
  const store = useUserStore();
  const { createItem, updateItem, deleteItem } = store;
  const { handleApiError } = useApiErrorHandler();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  const { confirm, modal: confirmModal } = useConfirmModal();

  const handleEdit = (user: User) => {
    setUserToEdit(user);
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (user: User) => {
    const ok = await confirm(`${user.name} adlı kullanıcıyı silmek istiyor musun?`);
    if (ok) {
      try {
        await deleteItem(user.id);
        toast.success("Kullanıcı silindi");
      } catch (error) {
        handleApiError(error);
      }
    }
  };

  const handleNew = () => {
    setUserToEdit(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setUserToEdit(null);
  };

  const handleSaveUser = async (userData: Omit<User, "id">) => {
    const {
      role,
      firm,
      account,
      firmName,
      accountName,
      roleName,
      ...cleanedData
    } = userData as any;

    const payload = {
      ...cleanedData,
      roleId: userData.roleId,
      firmId: userData.firmId,
      accountId: userData.accountId ?? null,
    };

    try {
      if (userToEdit) {
        await updateItem(userToEdit.id, payload);
        toast.success(`Kullanıcı Güncellendi: ${payload.name}`);
      } else {
        await createItem(payload);
        toast.success(`Yeni Kullanıcı Eklendi: ${payload.name}`);
      }
      store.fetchDataDynamic(store.queryParams); // Listeyi güncelleyelim
      handleCloseModal();
    } catch (error: any) {
      const errorMessage = error.message || "Kullanıcı kaydetme hatası oluştu.";
      toast.error(errorMessage);
    }
  };

  const actionBodyTemplate = (row: User) => (
    <TableActions
        actions={[
            {
                icon: Edit,
                colorClass: "text-blue-500",
                title: "Düzenle",
                onClick: () => handleEdit(row)
            },
            {
                icon: Trash2,
                colorClass: "text-red-400",
                title: "Sil",
                onClick: () => handleDeleteClick(row)
            }
        ]}
    />
  );

  const userColumns: DataTableColumn<User>[] = [
    { field: "id", header: "ID", width: "80px" },
    { field: "name", header: "Ad Soyad", width: "240px" },
    { field: "email", header: "E-posta", width: "240px" },
    {
      field: "firm.name",
      header: "Firma",
      width: "180px",
      body: (rowData: User) => {
        const text = rowData.accountId
          ? rowData.account?.name
          : rowData.firm?.name;
        return (
          <div className="truncate" title={text}>
            {rowData.accountId ? (
              text
            ) : (
              <span className="flex items-center gap-1">
                <CheckCheckIcon size={16} color="green" />
                {text}
              </span>
            )}
          </div>
        );
      }
    },
    { field: "role.name", header: "Rol", width: "160px", body: (e: User) => getTurkishRoleName(e.role?.name) },
    { field: "actions", header: "İşlemler", width: "80px", align: "center", body: actionBodyTemplate }
  ];

  return (
    <div className="h-full">
      {confirmModal}
      <EDataTable<User>
        tableKey="user_list"
        useStore={() => store}
        dataKey="id"
        title="Kullanıcı Yönetimi"
        placeholder="Ad, E-posta, Firma Ara..."
        searchFields={[
          { field: "name", operator: "contains" },
          { field: "email", operator: "contains" },
          { field: "firm.name", operator: "contains" },
          { field: "role.name", operator: "contains" },
        ]}
        actions={
          <IconTextButton
            onClick={() => handleNew()}
            icon={Plus}
            className="bg-primary-600 text-white hover:bg-primary-700 px-4 py-2 rounded-lg flex items-center gap-2"
          >
            Yeni Kullanıcı
          </IconTextButton>
        }
        columns={userColumns}
      />
      <UserDialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        user={userToEdit}
        onSave={handleSaveUser}
      />
    </div>
  );
};
